import { motion } from "framer-motion";
import enSaintsData from "~/data/about-us/saints.json";
import gujSaintsData from "~/data/about-us/saints_gu.json";
import { useFontStyle } from "~/hook/useFontStyle";
import useIntersectionObserver from "~/hook/useIntersectionObserver";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { cn } from "~/lib/utils";
import { BACKGROUND_IMAGE_STYLE } from "~/routes/$lang._index";

// Hero Section
const HeroSection = ({ saintsData }: { saintsData: typeof enSaintsData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="relative w-full mb-5"
    >
      <div className="w-full flex justify-center items-center px-4 md:px-6">
        <div className="w-full max-w-6xl text-center">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={isVisible ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h1 className={cn("text-3xl md:text-4xl font-bold text-textColor mb-4", medium)}>{saintsData.title}</h1>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-primaryColor to-transparent mx-auto mb-2" />
            <p className={cn("text-lg md:text-md text-textSecondaryColor italic", medium)}>{saintsData.subTitle}</p>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

// Introduction Section
const IntroductionSection = ({ saintsData }: { saintsData: typeof enSaintsData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, x: -30 }}
      animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="mb-10 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div>
          <p className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed text-justify", normalInter)}>
            {saintsData.introduction}
          </p>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Image Showcase Section
const ImageSection = ({ saintsData }: { saintsData: typeof enSaintsData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div className="relative rounded-2xl overflow-hidden shadow-2xl transition-all duration-500 group">
          {/* <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-10" /> */}
          <motion.img
            // whileHover={{ scale: 1.08 }}
            transition={{ duration: 0.6 }}
            src={saintsData.mainImage.src}
            alt={saintsData.mainImage.alt}
            className="w-full aspect-video object-cover"
            loading="lazy"
          />
          <div className="absolute bottom-0 left-0 right-0 z-20 p-6 md:p-8">
            <p className={cn("text-white text-lg font-semibold drop-shadow-lg capitalize", normalInter)}>{saintsData.mainImage.title}</p>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Vows Introduction
const VowsIntroSection = ({ saintsData }: { saintsData: typeof enSaintsData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.7 }}
      className="mb-12 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto text-center">
        <motion.h2
          initial={{ opacity: 0, y: -10 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -10 }}
          transition={{ delay: 0.2 }}
          className={cn("text-2xl md:text-3xl font-bold text-textColor inline-block border-b-4 border-primaryColor pb-3", medium)}
        >
          {saintsData.vartman.title}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0.4 }}
          className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed max-w-4xl mx-auto mt-6", normalInter)}
        >
          {saintsData.vartman.p1}
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0.4 }}
          className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed max-w-4xl mx-auto mt-6", normalInter)}
        >
          {saintsData.vartman.p2}
        </motion.p>
      </div>
    </motion.section>
  );
};

// Panch Vartman Cards Section
const PanchVartmanSection = ({ saintsData }: { saintsData: typeof enSaintsData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, rotateX: -15 },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <motion.section ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} variants={containerVariants} className="mb-12 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {saintsData.vartman.panchVartman.map((vow, index) => (
            <motion.div
              key={vow.key}
              variants={cardVariants}
              className="p-6 rounded-xl border-2 border-primaryColor/40 shadow-xl hover:border-primaryColor transition-all duration-300 group relative overflow-hidden"
            >
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-primaryColor/5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500" />

              {/* Title with Sanskrit term */}
              <h3
                className={cn(
                  "text-xl md:text-xl font-bold text-primaryColor mb-4 group-hover:text-textColor transition-colors duration-300 relative z-10",
                  medium,
                )}
              >
                {vow.key}
              </h3>

              {/* Description */}
              <p
                className={cn(
                  "text-textSecondaryColor text-sm md:text-base paragraph-text-size 2xl:text-lg leading-relaxed relative z-10",
                  normalInter,
                )}
              >
                {vow.value}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

// Vows Conclusion Section
const VowsConclusionSection = ({ saintsData }: { saintsData: typeof enSaintsData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.7 }}
      className="mb-10 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div className="text-center">
          <p className={cn("text-textSecondaryColor text-base leading-relaxed", normalInter)}>{saintsData.vartman.p3}</p>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Accomplishments Section
const AccomplishmentsSection = ({ saintsData }: { saintsData: typeof enSaintsData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, x: -40 }}
      animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
      transition={{ duration: 0.8 }}
      className="mb-12 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ delay: 0.2 }}
          className={cn("text-2xl md:text-3xl font-bold text-textColor mb-8 inline-block border-b-4 border-primaryColor pb-3", medium)}
        >
          {saintsData.accomplishments.title}
        </motion.h2>

        <motion.div className="relative">
          {/* Decorative side line */}

          <div className=" rounded-r-2xl transition-all duration-300">
            {saintsData.accomplishments.description.map((desc, index) => (
              <p
                key={index}
                className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed text-justify", normalInter)}
              >
                {desc}
              </p>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Modern Legacy Section
const ModernLegacySection = ({ saintsData }: { saintsData: typeof enSaintsData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0 }}
      transition={{ duration: 0.8 }}
      className="mb-12 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div className="p-10 md:p-14 text-center relative overflow-hidden">
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={isVisible ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="relative z-10"
          >
            <div className="w-20 h-20 bg-gradient-to-br from-primaryColor to-primaryColor/70 rounded-full flex items-center justify-center mb-6 mx-auto shadow-xl">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>

            <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor mb-6", medium)}>{saintsData.legacy.title}</h2>

            <p className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed", normalInter)}>
              {saintsData.legacy.description}
            </p>

            {/* Decorative dots */}
            <div className="flex justify-center gap-3 mt-8">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0 }}
                  animate={isVisible ? { scale: 1 } : { scale: 0 }}
                  transition={{ delay: 0.6 + i * 0.1, duration: 0.3 }}
                  className="w-3 h-3 bg-primaryColor rounded-full shadow-md"
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

function Saints() {
  const lang = useLanguageParams();
  const saintsData = lang === "en" ? enSaintsData : gujSaintsData;
  return (
    <div className="w-full bg-gradient-to-b from-white via-secondaryColor/5 to-white">
      {/* py-8 md:py-16 */}
      <div className="w-full ">
        <HeroSection saintsData={saintsData} />
        <IntroductionSection saintsData={saintsData} />
        <ImageSection saintsData={saintsData} />
        <VowsIntroSection saintsData={saintsData} />
        <PanchVartmanSection saintsData={saintsData} />
        <VowsConclusionSection saintsData={saintsData} />
        <AccomplishmentsSection saintsData={saintsData} />
        <ModernLegacySection saintsData={saintsData} />
      </div>
    </div>
  );
}

export default Saints;
